/* System defined core library */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { UtilityService } from '../../../../shared/services/utility.service';
import { AppointmentService } from '../../services/appointment/appointment.service';
import { MetricApiService } from '../../services/metric-api.service';
import { SelectedExamDetailService } from '../../services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../../shared/models/scheduling/appointmentSlot.model';
import { BatteryConfig } from '../../../../shared/models/scheduling/batteryConfig.model';
import { Client } from '../../../../shared/models/scheduling/client.model';
import { Exam } from '../../../../shared/models/scheduling/exam.model';
import { ExamSet } from '../../../../shared/models/scheduling/examSet.model';
import { Payment } from '../../../../shared/models/scheduling/payment.model';
import { Program } from '../../../../shared/models/scheduling/program.model';
import { SiteSlot } from '../../../../shared/models/scheduling/siteSlot.model';

/* User defined enums/constants */
import { ExamStatus, Fee } from '../../../../shared/enums/scheduling.enum';


@Component({
  selector: 'app-exam-set-selector',
  templateUrl: './exam-set-selector.component.html'
})
export class ExamSetSelectorComponent implements OnInit {
  /* User defined private/public variables */
  public selectedExams: Exam[] = [];
  public selectedAll: boolean = false;
  public selectedAppointments: AppointmentDetail[];
  public exam: Exam;
  public program: Program;
  public client: Client;
  public totalDuration: number = 0;
  public totalExamDuration: any;
  public examStatus = ExamStatus;
  public examSets: ExamSet[] = [];
  public multipleSets: boolean = false;
  public disableCheckbox: boolean = false;
  public batteryConfig: BatteryConfig;

  /* Input-OutPut parameters declarations */
  @Input() exams: Exam[];
  @Output() areExamsSelected: EventEmitter<boolean> = new EventEmitter();
  @Output() duration: EventEmitter<number> = new EventEmitter();

  /* Service subscriptions */
  program$: Subscription;
  exam$: Subscription;
  eligibility$: Subscription;
  client$: Subscription;
  selectedAppointments$: Subscription;
  translateLanguage$: Subscription;
  batteryConfig$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private appointmentService: AppointmentService,
    private metricApiService: MetricApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService,
    private utilityService: UtilityService
  ) {
    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.program$ = selectedExamDetailService.program.subscribe(
      program => {
        this.program = program;
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });

    this.batteryConfig$ = selectedExamDetailService.batteryConfig.subscribe(
      batteryConfig => {
        this.batteryConfig = batteryConfig;
      }
    );
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    this.exams.forEach((exam: Exam) => {
      if (!!exam.examSet.id) {
        const set = this.examSets.find(s => s.id === exam.examSet.id);
        if (!!set) {
          set.exams.push(exam);
        } else {
          this.examSets.push(new ExamSet(exam.examSet.id, exam.examSet.attributes, [exam]));
        }
      }
    });

    this.examSets.forEach((set: ExamSet) => {
      if (!this.appointmentService.canScheduleSetInFlow(set)) {
        this.metricApiService.logExamSetModalityConflict(set);
      }
    });

    if (!!this.selectedAppointments && !!this.exams) {
      this.setSelectedExams();
    } else {
      this.selectedExamDetailService.isBackToBackRequired = false;
      this.selectedExamDetailService.scheduleSetAttributeCode = false;
      this.selectedExamDetailService.examSetId = '';
      this.selectedExamDetailService.setBatteryConfig(null);
    }

    this.multipleSets = !!this.examSets.length && this.examSets[0].exams.length < this.exams.length;

    // pre-select set if there is only one set and exams are required to be scheduled together
    if (!this.multipleSets && !!this.examSets[0] && this.examSets[0].attributes.requiredSchedule) {
      this.disableCheckbox = true;
      this.setAllExamSetSelected(this.examSets[0], true);
    }

    this.validateState();
  }

  /*
   * checks or unchecks single exam
   * used on B2B exams when the user selects the exam-specific checkbox
   * @param exam {Exam}
   * @param event {any}
   */
  selectCheckboxExam(exam: Exam, targetObject: any): void {
    const isChecked: boolean = targetObject.target.checked;
    this.selectedExams = [];

    this.exams.forEach((e) => {
      if (e.examSet.id !== exam.examSet.id) {
        e.isSelected = false;
      }
      if (e.id === exam.id) {
        e.isSelected = isChecked;
      }
      if (e.isSelected) {
        this.selectedExams.push(e);
      }
    });

    this.setAppointmentDetails(this.selectedExams);

    this.validateState();
  }

  /*
   * selects single exam, unselecting any other exam
   * used on non-B2B exams when the user selects the radiobutton
   * @param event {boolean}
   */
  selectRadioBtnExam(exam: Exam): void {
    this.selectedExams = [];

    this.exams.forEach((e) => {
      e.isSelected = (exam.id === e.id);
      if (e.isSelected) {
        this.selectedExams.push(e);
      }
    });

    this.setAppointmentDetails(this.selectedExams);

    this.validateState();
  }

  /*
   * selects exam set, unselecting any other exam
   * used on B2B schedule required exam sets when the user selected the radio button
   * @param event {boolean}
   */
  selectRadioBtnSet(examSet: ExamSet): void {
    this.selectedExams = [];

    this.exams.forEach((e) => {
      e.isSelected = (e.examSet.id === examSet.id);
      if (e.isSelected) {
        this.selectedExams.push(e);
      }
    });

    this.setAppointmentDetails(this.selectedExams);

    this.validateState();
  }

  /*
   * takes in the selected exams to create appointment details array for SelectedAppointmentDetails
   * @param exams {Exam[]}
   */
  setAppointmentDetails(exams: Exam[]): void {
    this.selectedAppointments = [];
    exams.forEach((exam) => {
      const tempAppointmentDetail: AppointmentDetail = new AppointmentDetail(
        this.program,
        exam
      );
      tempAppointmentDetail.examName = exam.name;
      tempAppointmentDetail.client = this.client;
      tempAppointmentDetail.payments = [];
      tempAppointmentDetail.appointmentSlot = new AppointmentSlot();
      tempAppointmentDetail.appointmentSlot.site = new SiteSlot(null, null, null, null, null, null);
      const payment: Payment = new Payment(0, null, Fee.ExamFee);
      tempAppointmentDetail.payments.push(payment);
      this.selectedAppointments.push(tempAppointmentDetail);
      this.selectedExamDetailService.setFormInputData(null);
    });

    if (!!exams.length) {
      this.selectedExamDetailService.isBackToBackRequired = !!exams[0].examSet.attributes && !!exams[0].examSet.attributes.b2b;
      this.selectedExamDetailService.scheduleSetAttributeCode = !!exams[0].examSet.attributes && exams[0].examSet.attributes.requiredSchedule;
      this.selectedExamDetailService.examSetId = !!exams[0].examSet.id ? exams[0].examSet.id : '';

      const examSet: ExamSet = this.examSets.find(set => set.id === exams[0].examSet.id);
      const battery: BatteryConfig = !!examSet && !!examSet.batteryConfig ? examSet.batteryConfig : null;

      this.selectedExamDetailService.setBatteryConfig(battery);
    } else {
      this.selectedExamDetailService.isBackToBackRequired = false;
      this.selectedExamDetailService.scheduleSetAttributeCode = false;
      this.selectedExamDetailService.examSetId = '';
      this.selectedExamDetailService.setBatteryConfig(null);
    }

    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
  }

  /* Get Total exam duration and show validation if exceeds from max limit */
  setTotalDuration(): void {
    this.totalDuration = 0;

    if (!!this.selectedExams.length) {
      this.selectedExams.forEach((exam) => this.totalDuration += exam.duration);

      if (!!this.batteryConfig && !!this.batteryConfig.bufferTime) {
        this.totalDuration += this.batteryConfig.bufferTime;
      }
    }
    this.duration.emit(this.totalDuration);
  }

  /* disable exam if not schedulable using current method */
  disableExamInFlow(exam: Exam): boolean {
    return !this.appointmentService.canScheduleExamInFlow(exam);
  }

  isSetSchedulable(set: ExamSet): boolean {
    return this.appointmentService.canScheduleSetInFlow(set);
  }

  /* Set selected exams in component based on shared service */
  setSelectedExams(): void {
    if (!!this.selectedAppointments.length) {
      this.selectedAppointments.forEach(appointment => {

        this.exams.forEach(exm => {
          if (exm.id === appointment.exam.id) {
            exm.isSelected = true;
            this.selectedExams.push(exm);
          }
        });
      });

      const examSet = this.examSets.find(set => set.id === this.selectedAppointments[0].exam.examSet.id);
      if (!!examSet) {
        this.selectedExamDetailService.isBackToBackRequired = !!examSet.attributes && !!examSet.attributes.b2b;
        this.selectedExamDetailService.scheduleSetAttributeCode = !!examSet.attributes && !!examSet.attributes.requiredSchedule;
        this.selectedExamDetailService.examSetId = !!examSet.id ? examSet.id : '';

        const battery: BatteryConfig = !!examSet.batteryConfig ? examSet.batteryConfig : null;
        this.selectedExamDetailService.setBatteryConfig(battery);
      } else {
        this.selectedExamDetailService.isBackToBackRequired = false;
        this.selectedExamDetailService.scheduleSetAttributeCode = false;
        this.selectedExamDetailService.examSetId = '';
        this.selectedExamDetailService.setBatteryConfig(null);
      }
    }
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
    this.validateState();
  }

  /*
   * checks or unchecks all exams in a given set
   * used on init for single set B2B required or for the 'select examset' button
   * @param examSet {ExamSet}
   * @param event {boolean}
   */
  setAllExamSetSelected(examSet: ExamSet, event: boolean): void {
    // for B2B not required: if every exam in *that* set that is selectable in that flow is selected, 'Select all' acts as an unselect
    // for B2B required: on component load we get an event to select all if it's the only examset, and we don't want to select the exams if any of them are unschedulable
    if (
      (
        !!this.selectedExams &&
        !!this.selectedExams.length &&
        !!this.selectedExams[0] &&
        !!this.selectedExams[0].examSet &&
        this.selectedExams[0].examSet.id === examSet.id &&
        this.selectedExams.length === examSet.exams.filter(e => this.appointmentService.canScheduleExamInFlow(e)).length
      ) ||
      !this.appointmentService.canScheduleSetInFlow(examSet)
    ) {
      event = false;
    }

    this.selectedExams = [];


    this.exams.forEach((exam) => {
      if (event && exam.examSet.id === examSet.id && this.appointmentService.canScheduleExamInFlow(exam)) {
        exam.isSelected = event;
      } else {
        exam.isSelected = false;
      }
    });

    if (event) {
      this.selectedExams = this.selectedExams.concat(this.exams.filter(e => e.isSelected));
    }

    this.setAppointmentDetails(this.selectedExams);
    this.validateState();
  }

  validateState(): void {
    if (!!this.selectedAppointments) {
      this.setTotalDuration();
      this.selectedExamDetailService.setOverlap(this.selectedAppointments);
    }
    this.areExamsSelected.emit(!!this.selectedAppointments && !!this.selectedAppointments.length);
  }

  /*
   * takes in the total duration and returns object for template to seperately display hour and min
   * @param val {number}
   */
  durationToStr(val: number): any {
    return this.utilityService.durationToStr(val);
  }

  isExamSetInvalidScheduleMethod(examSet: ExamSet): boolean {
    return examSet.exams.some((e) => e.status === ExamStatus.InvalidSchedulingMethod.toString());
  }
}
